/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025-2026 (c) Miriam Mack, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import Api, { URLS } from 'Api/Api';
import type { Fair } from 'Api/generated';
import { AllowedRoles } from 'Contexts/Auth';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { BsPencil, BsPlus, BsTrash, BsUpload, BsX } from 'react-icons/bs';
import { getApiErrorMessage } from '../components/apiError';
import { FeedbackAlert, useFeedback } from '../components/Feedback';
import { FairsIcon } from '../components/UmatiIcons';
import styles from './Fairs.module.css';

type Position = { lat: number; lng: number };

type FairErrors = Partial<Record<'Name' | 'Tag' | 'From' | 'To' | 'Position', string>>;

const DATE_PATTERN = /^\d{2}\.\d{2}\.\d{4}$/;

const isValidDate = (value: string) => {
  if (!DATE_PATTERN.test(value)) return false;
  const [day, month, year] = value.split('.').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

const parseDate = (value: string) => {
  const [day, month, year] = value.split('.').map(Number);
  return new Date(year, month - 1, day);
};

const positionOf = (fair: Fair): Position | null => {
  const pos = fair.Position as Partial<Position> | null | undefined;
  if (!pos || typeof pos.lat !== 'number' || typeof pos.lng !== 'number') return null;
  return { lat: pos.lat, lng: pos.lng };
};

const validateFair = (draft: Fair, otherTags: string[]): FairErrors => {
  const errors: FairErrors = {};
  const tag = draft.Tag?.trim() ?? '';
  if (!draft.Name?.trim()) errors.Name = 'Please enter a name.';
  if (!tag) errors.Tag = 'Please enter a tag.';
  else if (!/^[a-z0-9-]+$/.test(tag)) errors.Tag = 'Use lowercase letters, digits and hyphens only.';
  else if (otherTags.some((t) => t.toLowerCase() === tag.toLowerCase())) errors.Tag = 'This tag is already in use.';

  const from = draft.From?.trim() ?? '';
  const to = draft.To?.trim() ?? '';
  if (from && !isValidDate(from)) errors.From = 'Please use the format DD.MM.YYYY.';
  if (to && !isValidDate(to)) errors.To = 'Please use the format DD.MM.YYYY.';
  if (!errors.From && !errors.To && from && to && parseDate(to) < parseDate(from)) {
    errors.To = 'The end date must not be before the start date.';
  }

  const pos = positionOf(draft);
  if (pos && (Math.abs(pos.lat) > 90 || Math.abs(pos.lng) > 180)) {
    errors.Position = 'Latitude must be between -90 and 90, longitude between -180 and 180.';
  }
  return errors;
};

const normalize = (draft: Fair): Fair => ({
  ...draft,
  Name: draft.Name?.trim(),
  Tag: draft.Tag?.trim(),
  From: draft.From?.trim() || undefined,
  To: draft.To?.trim() || undefined,
  Halls: (draft.Halls ?? []).map((h) => h.trim()).filter(Boolean),
});

const sortByName = (list: Fair[]) =>
  [...list].sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? '', undefined, { sensitivity: 'base' }));

type FairSidebarProps = {
  fairs: Fair[];
  selectedId: number | undefined;
  setSelectedId: (id: number | undefined) => void;
  onAdd: () => void;
};

const FairSidebar = ({ fairs, selectedId, setSelectedId, onAdd }: FairSidebarProps) => (
  <>
    <header className={styles['header-row']}>
      <h2 className="header">
        <FairsIcon className="icon" />
        Fairs
      </h2>
    </header>
    <nav className={styles['fairs-overview']} aria-label="Fairs">
      <ul>
        {fairs.map((fair) => (
          <li key={fair.Id}>
            <button
              type="button"
              className={styles['fair-button']}
              aria-current={selectedId === fair.Id}
              onClick={() => setSelectedId(fair.Id)}
            >
              {fair.Name ?? fair.Tag ?? String(fair.Id)}
              {fair.Hidden && (
                <span className={styles['hidden-badge']} title="Not shown in the umati dashboard">
                  hidden
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </nav>
    <Button size="sm" variant="outline-primary" className="w-100 mt-2" onClick={onAdd}>
      <BsPlus /> Add fair
    </Button>
  </>
);

type FairFormProps = {
  draft: Fair;
  setDraft: React.Dispatch<React.SetStateAction<Fair>>;
  errors: FairErrors;
  showErrors: boolean;
  tagLocked?: boolean;
};

const FairForm = ({ draft, setDraft, errors, showErrors, tagLocked = false }: FairFormProps) => {
  const [newHall, setNewHall] = useState('');
  const set = (field: keyof Fair, value: string | boolean) => setDraft((d) => ({ ...d, [field]: value }));
  const invalid = (field: keyof FairErrors) => showErrors && !!errors[field];
  const position = positionOf(draft);
  const halls = draft.Halls ?? [];
  const hallExists = halls.some((h) => h.toLowerCase() === newHall.trim().toLowerCase());

  const addHall = () => {
    const hall = newHall.trim();
    if (!hall || hallExists) return;
    setDraft((d) => ({ ...d, Halls: [...(d.Halls ?? []), hall] }));
    setNewHall('');
  };

  const setPosition = (key: keyof Position, raw: string) => {
    setDraft((d) => {
      const current = positionOf(d) ?? { lat: 0, lng: 0 };
      if (raw === '' && key === 'lat' && current.lng === 0) return { ...d, Position: null };
      const num = Number.parseFloat(raw);
      return { ...d, Position: { ...current, [key]: Number.isNaN(num) ? 0 : num } };
    });
  };

  return (
    <>
      <div className="row">
        <Form.Group className="col-md-7 mb-3" controlId="fair-name">
          <Form.Label>Name</Form.Label>
          <Form.Control
            value={draft.Name ?? ''}
            onChange={(e) => set('Name', e.target.value)}
            isInvalid={invalid('Name')}
          />
          <Form.Control.Feedback type="invalid">{errors.Name}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="col-md-5 mb-3" controlId="fair-tag">
          <Form.Label>Tag</Form.Label>
          <Form.Control
            value={draft.Tag ?? ''}
            onChange={(e) => set('Tag', e.target.value)}
            isInvalid={invalid('Tag')}
            disabled={tagLocked}
            placeholder="e.g. hmi"
          />
          <Form.Text muted>
            {tagLocked
              ? 'The tag cannot be changed because the map file is stored under it.'
              : 'Short identifier used in URLs and file names.'}
          </Form.Text>
          <Form.Control.Feedback type="invalid">{errors.Tag}</Form.Control.Feedback>
        </Form.Group>
      </div>

      <div className="row">
        <Form.Group className="col-md-6 mb-3" controlId="fair-from">
          <Form.Label>Start date</Form.Label>
          <Form.Control
            value={draft.From ?? ''}
            onChange={(e) => set('From', e.target.value)}
            isInvalid={invalid('From')}
            placeholder="DD.MM.YYYY"
          />
          <Form.Control.Feedback type="invalid">{errors.From}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="col-md-6 mb-3" controlId="fair-to">
          <Form.Label>End date</Form.Label>
          <Form.Control
            value={draft.To ?? ''}
            onChange={(e) => set('To', e.target.value)}
            isInvalid={invalid('To')}
            placeholder="DD.MM.YYYY"
          />
          <Form.Control.Feedback type="invalid">{errors.To}</Form.Control.Feedback>
        </Form.Group>
      </div>

      <div className="row">
        <Form.Group className="col-md-6 mb-3" controlId="fair-lat">
          <Form.Label>Latitude</Form.Label>
          <Form.Control
            type="number"
            step="any"
            value={position?.lat ?? ''}
            onChange={(e) => setPosition('lat', e.target.value)}
            isInvalid={invalid('Position')}
          />
        </Form.Group>
        <Form.Group className="col-md-6 mb-3" controlId="fair-lng">
          <Form.Label>Longitude</Form.Label>
          <Form.Control
            type="number"
            step="any"
            value={position?.lng ?? ''}
            onChange={(e) => setPosition('lng', e.target.value)}
            isInvalid={invalid('Position')}
          />
          <Form.Control.Feedback type="invalid">{errors.Position}</Form.Control.Feedback>
        </Form.Group>
      </div>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="fair-new-hall">Halls</Form.Label>
        {halls.length > 0 && (
          <div className="d-flex flex-wrap gap-1 mb-2">
            {halls.map((hall) => (
              <span key={hall} className={styles['hall-badge']}>
                {hall}
                <button
                  type="button"
                  className={styles['hall-remove']}
                  aria-label={`Remove hall ${hall}`}
                  onClick={() => setDraft((d) => ({ ...d, Halls: (d.Halls ?? []).filter((h) => h !== hall) }))}
                >
                  <BsX />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="d-flex gap-2">
          <Form.Control
            id="fair-new-hall"
            size="sm"
            placeholder="Add hall, e.g. 9"
            value={newHall}
            onChange={(e) => setNewHall(e.target.value)}
            isInvalid={hallExists}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addHall();
              }
            }}
          />
          <Button size="sm" variant="outline-primary" disabled={!newHall.trim() || hallExists} onClick={addHall}>
            Add
          </Button>
        </div>
        {hallExists && <div className="invalid-feedback d-block">This hall is already in the list.</div>}
      </Form.Group>

      <Form.Check
        type="switch"
        id="fair-hidden"
        label="Hidden (not shown in the dashboard)"
        checked={draft.Hidden ?? false}
        onChange={(e) => set('Hidden', e.target.checked)}
      />
    </>
  );
};

type FairDetailProps = {
  fair: Fair;
  allFairs: Fair[];
  onUpdate: (updated: Fair) => void;
  onDelete: (id: number) => void;
};

const FairDetail = ({ fair, allFairs, onUpdate, onDelete }: FairDetailProps) => {
  const [draft, setDraft] = useState<Fair>({ ...fair });
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [editServerError, setEditServerError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mapVersion, setMapVersion] = useState(0);
  const logoRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<HTMLInputElement>(null);
  const { feedback, success, error } = useFeedback();

  useEffect(() => {
    setDraft({ ...fair });
  }, [fair]);

  const otherTags = allFairs.filter((f) => f.Id !== fair.Id).map((f) => f.Tag ?? '');
  const errors = validateFair(draft, otherTags);
  const isValid = Object.keys(errors).length === 0;
  const isDirty = JSON.stringify(normalize(draft)) !== JSON.stringify(normalize(fair));

  const closeEdit = () => {
    setShowEditDialog(false);
    setShowErrors(false);
    setEditServerError(null);
    setDraft({ ...fair });
  };

  const handleSave = async () => {
    setShowErrors(true);
    setEditServerError(null);
    if (!isValid || fair.Id === undefined) return;
    const payload = normalize(draft);
    try {
      await Api.Fairs.putFair(fair.Id, payload);
      onUpdate(payload);
      closeEdit();
      success('Changes saved.');
    } catch (err) {
      setEditServerError(getApiErrorMessage(err, 'The changes could not be saved.'));
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    if (fair.Id === undefined) return;
    try {
      await Api.Fairs.deleteFair(fair.Id);
      onDelete(fair.Id);
    } catch (err) {
      error(err, 'The fair could not be deleted.');
    }
  };

  const uploadLogo = async (file: File) => {
    if (fair.Id === undefined) return;
    try {
      const { data: updated } = await Api.Fairs.putFairImage(fair.Id, file);
      onUpdate(updated);
      success('Logo uploaded.');
    } catch (err) {
      error(err, 'The logo could not be uploaded.');
    }
  };

  const uploadMap = async (file: File) => {
    if (fair.Id === undefined) return;
    try {
      const { data: updated } = await Api.Fairs.putFairMap(fair.Id, file);
      onUpdate(updated);
      setMapVersion((v) => v + 1);
      success('Map uploaded.');
    } catch (err) {
      error(err, 'The map could not be uploaded.');
    }
  };

  const position = positionOf(fair);

  return (
    <>
      <div className={styles['name-header']}>
        <h4 className="mb-0">{fair.Name ?? fair.Tag}</h4>
        <Button size="sm" variant="outline-primary" onClick={() => setShowEditDialog(true)}>
          <BsPencil className="me-1" />
          Edit
        </Button>
      </div>
      <p className={styles['name-subheading']}>Tag: {fair.Tag}</p>

      <FeedbackAlert feedback={feedback} />

      <Table bordered size="sm" className={styles.table}>
        <tbody>
          <tr>
            <th>Dates</th>
            <td>
              {fair.From || fair.To ? (
                `${fair.From ?? '?'} to ${fair.To ?? '?'}`
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
          </tr>
          <tr>
            <th>Halls</th>
            <td>{fair.Halls?.length ? fair.Halls.join(', ') : <span className="text-muted">-</span>}</td>
          </tr>
          <tr>
            <th>Location</th>
            <td>
              {position ? (
                <a
                  href={`https://www.openstreetmap.org/?mlat=${position.lat}&mlon=${position.lng}#map=15/${position.lat}/${position.lng}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {position.lat}, {position.lng}
                </a>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
          </tr>
          <tr>
            <th>Visibility</th>
            <td>{fair.Hidden ? 'Hidden' : 'Shown in the dashboard'}</td>
          </tr>
        </tbody>
      </Table>

      <h6 className="mt-4 mb-2">Images</h6>
      <div className={styles['images-row']}>
        <div className={styles['image-card']}>
          <span className={styles['image-label']}>Logo</span>
          {fair.ImageFile ? (
            <img
              className={styles['fair-image']}
              src={URLS.Fairs.getLogo(fair.ImageFile)}
              alt={`${fair.Name ?? ''} logo`}
            />
          ) : (
            <div className={styles['image-placeholder']}>No logo</div>
          )}
          <Button size="sm" variant="outline-secondary" onClick={() => logoRef.current?.click()}>
            <BsUpload className="me-1" />
            {fair.ImageFile ? 'Replace' : 'Upload'}
          </Button>
          <input
            type="file"
            accept="image/*"
            hidden
            ref={logoRef}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadLogo(file);
              e.target.value = '';
            }}
          />
        </div>

        <div className={styles['image-card']}>
          <span className={styles['image-label']}>Hall map</span>
          {fair.Tag ? (
            <img
              key={mapVersion}
              className={styles['fair-image']}
              src={URLS.Fairs.getMap(fair.Tag)}
              alt={`${fair.Name ?? ''} map`}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('d-none');
              }}
            />
          ) : null}
          <div className={`${styles['image-placeholder']} d-none`}>No map</div>
          <Button size="sm" variant="outline-secondary" onClick={() => mapRef.current?.click()}>
            <BsUpload className="me-1" />
            Upload map
          </Button>
          <input
            type="file"
            accept="image/svg+xml,image/*"
            hidden
            ref={mapRef}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadMap(file);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <div className="d-flex mt-4">
        <Button size="sm" variant="outline-danger" className="ms-auto" onClick={() => setShowDeleteConfirm(true)}>
          <BsTrash className="me-1" />
          Delete fair
        </Button>
      </div>

      <Modal show={showEditDialog} onHide={closeEdit} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit fair</Modal.Title>
        </Modal.Header>
        <Form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <Modal.Body>
            <FairForm draft={draft} setDraft={setDraft} errors={errors} showErrors={showErrors} tagLocked />
            {editServerError && <div className="alert alert-danger py-2 mt-3 mb-0">{editServerError}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeEdit}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!isDirty}>
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete fair</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Do you really want to delete <strong>{fair.Name ?? fair.Tag}</strong>? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

type AddFairModalProps = {
  show: boolean;
  allFairs: Fair[];
  onClose: () => void;
  onCreated: (fair: Fair) => void;
};

const emptyFair = (): Fair => ({ Id: 0, Name: '', Tag: '', Halls: [], Hidden: false });

const AddFairModal = ({ show, allFairs, onClose, onCreated }: AddFairModalProps) => {
  const [draft, setDraft] = useState<Fair>(emptyFair);
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = validateFair(
    draft,
    allFairs.map((f) => f.Tag ?? ''),
  );
  const isValid = Object.keys(errors).length === 0;

  const close = () => {
    setDraft(emptyFair());
    setShowErrors(false);
    setServerError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    setServerError(null);
    if (!isValid) return;
    setSubmitting(true);
    try {
      const { Id: _ignored, ...payload } = normalize(draft);
      const { data: created } = await Api.Fairs.postFair(payload as Fair);
      onCreated(created);
      close();
    } catch (err) {
      setServerError(getApiErrorMessage(err, 'The fair could not be created.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={close} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add fair</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <FairForm draft={draft} setDraft={setDraft} errors={errors} showErrors={showErrors} />
          <p className="text-muted small mb-0 mt-3">
            Logo and hall map can be uploaded after the fair has been created.
          </p>
          {serverError && <div className="alert alert-danger py-2 mt-3 mb-0">{serverError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Adding…' : 'Add fair'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const FairPage = () => {
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [showAdd, setShowAdd] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    Api.Fairs.getFairs()
      .then(({ data }) => {
        const sorted = sortByName(data);
        setFairs(sorted);
        if (sorted.length > 0) setSelectedId(sorted[0].Id);
      })
      .catch((err) => setLoadError(getApiErrorMessage(err, 'The fairs could not be loaded.')));
  }, []);

  const selectedFair = fairs.find((f) => f.Id === selectedId);

  const handleUpdate = (updated: Fair) => {
    setFairs((prev) => sortByName(prev.map((f) => (f.Id === updated.Id ? { ...f, ...updated } : f))));
  };

  const handleDelete = (id: number) => {
    setFairs((prev) => {
      const remaining = prev.filter((f) => f.Id !== id);
      setSelectedId((current) => (current === id ? remaining[0]?.Id : current));
      return remaining;
    });
  };

  const handleCreated = (created: Fair) => {
    setFairs((prev) => sortByName([...prev, created]));
    setSelectedId(created.Id);
  };

  return (
    <AllowedRoles roles={['SuperAdmin', 'Admin']}>
      <div className={styles['fairs-page']}>
        <aside className={styles['fairs-sidebar']}>
          <FairSidebar
            fairs={fairs}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            onAdd={() => setShowAdd(true)}
          />
        </aside>

        <main className={styles['fairs-content']}>
          {loadError && <div className="alert alert-danger">{loadError}</div>}

          {!loadError && fairs.length === 0 && (
            <div className="text-muted">
              <h5>No fairs yet</h5>
              <p>Use "Add fair" to create the first entry.</p>
            </div>
          )}

          {selectedFair && (
            <FairDetail
              key={selectedFair.Id}
              fair={selectedFair}
              allFairs={fairs}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          )}
        </main>
      </div>

      <AddFairModal show={showAdd} allFairs={fairs} onClose={() => setShowAdd(false)} onCreated={handleCreated} />
    </AllowedRoles>
  );
};

export default FairPage;
